import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { createMemoryStorage } from './memory-storage'
import type { Database } from './types'

/**
 * Supabase browser client (PKCE flow).
 *
 * Env is read from the REPO-ROOT `.env` (see `envDir` in vite.config.ts).
 * Never hardcode project URLs/keys — a sibling repo leaked service keys into
 * git history; do not copy that pattern. The anon key is safe to ship ONLY
 * because every table has RLS enabled (supabase/migrations/0002_rls.sql).
 */
export function createSupabaseClient(): SupabaseClient<Database> {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  if (!url || !anonKey) {
    throw new Error(
      'Missing Supabase configuration. Copy .env.example to .env at the repo root and set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    )
  }

  return createClient<Database>(url, anonKey, {
    auth: {
      // PKCE keeps the OAuth/email-link exchange safe from code interception.
      flowType: 'pkce',
      // Tokens stay in memory only — see memory-storage.ts (Phase 2: cookies).
      storage: createMemoryStorage(),
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  })
}

let client: SupabaseClient<Database> | null = null

/** Lazy singleton so tests can run without env vars present. */
export function getSupabase(): SupabaseClient<Database> {
  if (!client) client = createSupabaseClient()
  return client
}
